import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../context/AuthContext';
import apiClient from '../utils/api';
import { formatCurrency } from '../utils/formatters';

const statusStyle = {
  paid: 'badge-paid',
  sent: 'badge-sent',
  overdue: 'badge-overdue',
  draft: 'badge-draft',
};

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, invoiceId: null });
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isOwner = hasRole(ROLES.BUSINESS_OWNER);

  React.useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      const res = await apiClient.get('/invoices');
      setInvoices(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const confirmDelete = (id, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, invoiceId: id });
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/invoices/${deleteModal.invoiceId}`);
      setInvoices(prev => prev.filter(inv => inv.id !== deleteModal.invoiceId));
      setDeleteModal({ isOpen: false, invoiceId: null });
    } catch {
      alert('Failed to delete invoice.');
      setDeleteModal({ isOpen: false, invoiceId: null });
    }
  };

  const filtered = invoices.filter(inv =>
    inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.status?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="invoices-loading">
        <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="invoice-list">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="invoice-list-title">Invoices</h1>
          <p className="text-slate-500 text-sm mt-1">
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
            {!isOwner && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">View Only</span>}
          </p>
        </div>
        {isOwner && (
          <button onClick={() => navigate('/invoices/new')}
            className="btn-primary flex items-center gap-2"
            data-testid="create-invoice-button">
            <span>+</span> New Invoice
          </button>
        )}
      </div>

      {invoices.length === 0 ? (
        <div className="card p-16 text-center" data-testid="empty-invoices">
          <div className="text-5xl mb-4">📄</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No invoices yet</h3>
          <p className="text-slate-500 mb-6">{isOwner ? 'Create your first invoice to get started' : 'No invoices found'}</p>
          {isOwner && (
            <button onClick={() => navigate('/invoices/new')}
              className="btn-primary" data-testid="create-first-invoice-button">
              Create Invoice
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-modern max-w-xs"
            />
          </div>

          <div className="card overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice #</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Issue Date</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`invoice-row-${invoice.id}`}>
                    <td className="px-6 py-4 text-sm font-semibold text-indigo-600"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}>
                      {invoice.invoice_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{invoice.issue_date}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{invoice.due_date}</td>
                    <td className="px-6 py-4 text-sm font-bold text-slate-900">{formatCurrency(invoice.total, invoice.currency)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusStyle[invoice.status] || 'badge-draft'}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate(`/invoices/${invoice.id}`)}
                          data-testid={`view-invoice-button-${invoice.id}`}
                          className="text-xs font-medium px-3 py-1 rounded-lg transition-colors"
                          style={{ color: '#6366f1', background: '#eef2ff' }}>
                          View
                        </button>
                        {isOwner && (
                          <>
                            <button onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
                              data-testid={`edit-invoice-button-${invoice.id}`}
                              className="text-xs font-medium px-3 py-1 rounded-lg transition-colors"
                              style={{ color: '#475569', background: '#f1f5f9' }}>
                              Edit
                            </button>
                            <button onClick={e => confirmDelete(invoice.id, e)}
                              data-testid={`delete-invoice-button-${invoice.id}`}
                              className="text-xs font-medium px-3 py-1 rounded-lg transition-colors"
                              style={{ color: '#ef4444', background: '#fef2f2' }}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && search && (
              <p className="text-center py-8 text-slate-400">No invoices match "{search}"</p>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl transform transition-all scale-100 animate-slide-up">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Invoice?</h3>
            <p className="text-slate-500 text-sm mb-6">
              Are you sure you want to delete this invoice? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ isOpen: false, invoiceId: null })}
                className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-500 hover:bg-red-600 shadow-sm shadow-red-200 transition-colors"
              >
                Delete Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;